import React, { Dispatch, SetStateAction } from 'react';

import NewInput from '../../../../components/NewInput/NewInput';
import NewSelect from '../../../../components/NewSelect/NewSelect';
import NewTypeahead from '../../../../components/NewTypeahead/NewTypeahead';
import NewDatePicker from '../../../../components/NewDatePicker/NewDatePicker';
import { EnhancedErrorType } from './SliderErrors';

interface SliderErrorsInputSwitchType {
  errorId: string;
  inputType: string;
  colSourceOptions: EnhancedErrorType['colSourceOptions'];
  inputId: string;
  inputValue: string | undefined;
  setInputValue: Dispatch<SetStateAction<string>>;
  onValueInput: (errorId: string, value?: string) => void;
}

const SliderErrorsInputSwitch = (props: SliderErrorsInputSwitchType) => {
  const { errorId, inputType, colSourceOptions, inputId, inputValue, setInputValue, onValueInput } = props;

  if (inputValue === undefined) return <></>;

  let options: string[] = [];

  if (typeof colSourceOptions === 'function') {
    colSourceOptions.bind(this)(inputValue, items => {
      options = items;
    });
  } else if (colSourceOptions) {
    options = colSourceOptions.map(v => String(v));
  }

  const handleInputChange = e => {
    setInputValue(e.target.value);
    onValueInput(errorId, e.target.value);
  };

  const inputProps = {
    id: inputId,
    onChange: handleInputChange,
    value: inputValue,
  };

  const typeaheadFilteredOptions = value =>
    options.filter(option => option.toLowerCase().includes(inputValue.toLowerCase()));

  // autoFocus was removed from the input components below because it stealing focus even if the user
  // selects a cell without an error. This was causing the user to have to click twice to select a cell.
  // This is a temporary fix until we can introduce the correct behavior for error slider, which is:
  // 1. If the user selects a cell with an error, the specific slider error should twiddle open, scrolled to, and input focused.
  // 2. If the user selects a cell with no error, the focus should remain on the cell
  switch (inputType) {
    case 'datepicker':
      return (
        <NewDatePicker
          {...inputProps}
          onChange={(_, { value }) => {
            setInputValue(value);
            onValueInput(errorId, value);
          }}
        />
      );
    case 'typeahead':
      return (
        <NewTypeahead
          {...inputProps}
          options={options}
          optionLabel={option => option}
          filterOptions={typeaheadFilteredOptions}
          onChange={(_, { value }) => {
            setInputValue(value);
            onValueInput(errorId, value);
          }}
          width="full"
          dropdownWidth="100%"
          input={props => <NewInput {...props} id={`${props.id}-typeahead`} />}
        />
      );
    case 'dropdown':
      return (
        <NewSelect {...inputProps} width="full">
          <NewSelect.Option value="" />
          {options.map(option => (
            <NewSelect.Option key={`${inputProps.id}-dropdown-${option}`} value={option}>
              {option}
            </NewSelect.Option>
          ))}
        </NewSelect>
      );
    default:
      return <NewInput {...inputProps} width="full" />;
  }
};

export default SliderErrorsInputSwitch;
